import axios from "axios";
import { API_BASE_URL, API_HEADERS } from "../apiConfig";
import { API_ENDPOINTS } from "../apiEndpoints";

/**
 * POST order/checkout - submit order after Stripe success.
 * @param {object} payload - Checkout payload (CustomerID, CustomerName, UserName, OrderDate, TotalAmount, TaxAmount, ShippingAmount, ItemDetail, PaymentMethod, CreditCardDetail, ShippingDetail, BillingDetail, PaymentProcesserResponse)
 * @param {object} [options] - Optional: { userName } to set UserName header (logged-in user email); otherwise uses API_HEADERS.UserName
 * @returns {Promise<object>} API response (e.g. OrderId, success)
 */
export const postCheckout = async (payload, options = {}) => {
  const url = `${API_BASE_URL}/${API_ENDPOINTS.POST_OrderCheckout}`;
  const userName = options.userName ?? payload?.UserName ?? API_HEADERS.UserName;
  const response = await axios.post(url, payload, {
    headers: {
      Token: API_HEADERS.Token,
      UserName: userName,
      "Content-Type": "application/json",
    },
  });
  return response.data;
};

/**
 * POST order/validateorderitems - validates checkout item quantities before payment modal opens.
 * @param {object} payload - Payload shape: { orderItems: [{ itemId, itemName, quantity }] }
 * @param {object} [options] - Optional: { userName } to set UserName header.
 * @returns {Promise<object>} API response.
 */
export const validateOrderItems = async (payload, options = {}) => {
  const url = `${API_BASE_URL}/${API_ENDPOINTS.POST_ValidateOrderItems}`;
  const userName = options.userName ?? API_HEADERS.UserName;
  const response = await axios.post(url, payload, {
    headers: {
      Token: API_HEADERS.Token,
      UserName: userName,
      "Content-Type": "application/json",
    },
  });
  return response.data;
};

/**
 * POST order/CreatePaymentIntent - creates Stripe payment intent via backend API.
 * Uses local checkout API endpoint provided for Stripe intent orchestration.
 * @param {object} payload - Payload shape: { IsTestMode, Currency, Amount }
 * @returns {Promise<object>} API response.
 */
export const createPaymentIntent = async (payload) => {
  const url = `${API_BASE_URL}/${API_ENDPOINTS.POST_CreatePaymentIntent}`;
  const response = await axios.post(url, payload, {
    headers: {
      Token: API_HEADERS.Token,
      UserName: API_HEADERS.UserName,
      Cookie: API_HEADERS.Cookie,
      "Content-Type": "application/json",
    },
  });
  return response.data;
};

/**
 * GET order/list?CustomerId=&pageNumber=&pageSize=
 * Headers: Token, UserName (logged-in email).
 * @returns {Promise<{ Pagination: { PageNumber, PageSize, TotalCount }, Result: array }>}
 */
export const getOrderList = async (customerId, pageNumber, pageSize, userName) => {
  const url = `${API_BASE_URL}/${API_ENDPOINTS.GET_OrderList}`;
  const response = await axios.get(url, {
    params: {
      CustomerId: customerId,
      pageNumber,
      pageSize,
    },
    headers: {
      Token: API_HEADERS.Token,
      UserName: userName || API_HEADERS.UserName,
    },
  });
  return response.data;
};

/**
 * GET order/detail?TxnNumber= — full order with ItemDetail, ShippingDetail, BillingDetail.
 */
export const getOrderDetail = async (txnNumber, userName) => {
  const url = `${API_BASE_URL}/${API_ENDPOINTS.GET_OrderDetail}`;
  const response = await axios.get(url, {
    params: { TxnNumber: txnNumber },
    headers: {
      Token: API_HEADERS.Token,
      UserName: userName || API_HEADERS.UserName,
    },
  });
  return response.data;
};

/**
 * POST order/GetShippingRates - fetch carrier shipping rate quotes.
 * @param {object} payload - Shipping rate request payload.
 * @param {object} [options] - Optional: { userName } to set UserName header.
 * @returns {Promise<object>} API response with rates array.
 */
export const getShippingRates = async (payload, options = {}) => {
  const url = `${API_BASE_URL}/${API_ENDPOINTS.POST_GetShippingRates}`;
  const userName = options.userName ?? API_HEADERS.UserName;
  const response = await axios.post(url, payload, {
    headers: {
      Token: API_HEADERS.Token,
      UserName: userName,
      Cookie: API_HEADERS.Cookie,
      "Content-Type": "application/json",
    },
  });
  return response.data;
};

export const orderService = {
  postCheckout,
  validateOrderItems,
  createPaymentIntent,
  getOrderList,
  getOrderDetail,
  getShippingRates,
};
